import { useState } from "react";
import { toast } from "sonner";
import { FileText, Trash2, Upload } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const BOTTLE_SIZES = ["375ml", "750ml", "1.5L", "3L", "4.5L", "5L", "6L", "9L", "12L", "15L", "18L", "27L", "73cl", "70cl", "68cl", "62cl"];
export const FILL_LEVELS = ["In Neck", "Base Neck", "Very Top Shoulder", "Top Shoulder", "High Shoulder", "High to Mid-Shoulder", "Mid-Shoulder", "Low Shoulder", "Below Low Shoulder", "less than 1 cm",
  "1cm", "1.5cm", "2cm", "2.5cm", "3cm", "3.5cm", "4cm", "4.5cm", "5cm", "5.5cm", "6cm", "6.5cm", "7cm", "7.5cm", "below 7.5cm"];
export const CONDITIONS = ["Excellent", "Very Good", "Good", "Average", "Below average", "Poor", "Very Poor", "Missing"];
export const ORIGIN_FIELDS = [["country", "Country"], ["region", "Region"], ["sub_region", "Sub-region"], ["site", "Site"], ["parcel", "Parcel"], ["classification", "Classification"], ["designation", "Designation"], ["wine_type", "Type"], ["sub_type", "Sub type"]];
export const EXTRA_DEFAULTS = { serial_number: "", bottle_size: "750ml", fill_level: "", label_condition: "", capsule_condition: "", condition_other: "", catalog_name: "", ...Object.fromEntries(ORIGIN_FIELDS.map(([k]) => [k, ""])) };

export const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <label className="block text-[11px] uppercase tracking-widest text-[#6E665D] mb-1.5">{label}</label>
    {children}
  </div>
);

export const OptionSelect = ({ value, onChange, options, testId, placeholder = "Not specified", allowEmpty = true }) => (
  <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? "" : v)}>
    <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent className="max-h-72">
      {allowEmpty && <SelectItem value="none">{placeholder}</SelectItem>}
      {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const OriginFields = ({ f, set }) => (
  <>
    <p className="col-span-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] pt-2">Origin</p>
    {ORIGIN_FIELDS.map(([k, label]) => (
      <Field key={k} label={label}><Input value={f[k] || ""} onChange={set(k)} data-testid={`wine-${k.replace("_", "-")}-input`} /></Field>
    ))}
  </>
);

export const ConditionFields = ({ f, setF }) => (
  <>
    <p className="col-span-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] pt-2">Bottle condition (optional)</p>
    <Field label="Fill level"><OptionSelect value={f.fill_level} onChange={(v) => setF({ ...f, fill_level: v })} options={FILL_LEVELS} testId="wine-fill-level-select" /></Field>
    <Field label="Label condition"><OptionSelect value={f.label_condition} onChange={(v) => setF({ ...f, label_condition: v })} options={CONDITIONS} testId="wine-label-condition-select" /></Field>
    <Field label="Capsule condition"><OptionSelect value={f.capsule_condition} onChange={(v) => setF({ ...f, capsule_condition: v })} options={CONDITIONS} testId="wine-capsule-condition-select" /></Field>
    <Field label="Other"><Input value={f.condition_other || ""} onChange={(e) => setF({ ...f, condition_other: e.target.value })} data-testid="wine-condition-other-input" /></Field>
  </>
);

export const openDocument = async (wineId, path = `/wines/${wineId}/document`) => {
  const tab = window.open("", "_blank");
  try {
    const { data } = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    if (tab) tab.location.href = url;
    else window.location.href = url;
  } catch (e) {
    if (tab) tab.close();
    toast.error("Could not open the document");
  }
};

export const DocumentField = ({ wine, file, setFile, onChanged }) => {
  const [doc, setDoc] = useState(wine?.purchase_document || null);
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const picked = e.target.files[0];
    e.target.value = "";
    if (!picked) return;
    if (!["application/pdf", "image/jpeg", "image/png"].includes(picked.type)) return toast.error("Only PDF, JPEG or PNG files");
    if (picked.size > 10 * 1024 * 1024) return toast.error("The file is larger than 10 MB");
    if (!wine) return setFile(picked);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", picked);
      const { data } = await api.post(`/wines/${wine.id}/document`, fd);
      setDoc(data);
      toast.success("Document attached");
      onChanged?.();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!wine) return setFile(null);
    try {
      await api.delete(`/wines/${wine.id}/document`);
      setDoc(null);
      toast.success("Document removed");
      onChanged?.();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const name = wine ? doc?.filename : file?.name;
  return (
    <Field label="Purchase document (PDF, JPEG or PNG)">
      {name ? (
        <div className="flex items-center gap-2 h-10 rounded-md border border-[#2E252A] px-3 text-sm" data-testid="purchase-document-attached">
          <FileText className="w-4 h-4 text-[#D4AF37] shrink-0" />
          {wine ? <button type="button" onClick={() => openDocument(wine.id)} className="flex-1 truncate text-left text-[#F5F0EB] hover:text-[#D4AF37]" data-testid="purchase-document-open">{name}</button>
            : <span className="flex-1 truncate text-[#F5F0EB]">{name}</span>}
          <button type="button" onClick={remove} className="text-[#6E665D] hover:text-[#8C2B36]" data-testid="purchase-document-remove" aria-label="Remove"><Trash2 className="w-4 h-4" /></button>
        </div>
      ) : (
        <label className="flex items-center gap-2 h-10 rounded-md border border-dashed border-[#4A3B43] px-3 text-sm text-[#A89F91] cursor-pointer hover:border-[#D4AF37]">
          <input type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" onChange={pick} data-testid="purchase-document-input" />
          <Upload className="w-4 h-4" />{busy ? "Uploading…" : "Attach invoice or receipt"}
        </label>
      )}
    </Field>
  );
};
